import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import {
  Box,
  List,
  ListItem,
  ListItemText,
  Button,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import PrintIcon from "@mui/icons-material/Print";
import ConsultationForm from "./ConsultationForm";

interface ConsultationListProps {
  patientId?: number;
}

// API Configuration
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5000/api";
const API_ENDPOINTS = {
  consultations: "/Consultatii",
  getByPatient: "/Consultatii",
};

// Mock consultation data generator
const generateMockConsultations = (patientId?: number): any[] => {
  const diagnostics = [
    "Hipertensiune arterială",
    "Diabet zaharat tip 2",
    "Infecție respiratorie superioară",
    "Gastroenterită acută",
    "Migrena cronică",
    "Artrită reumatoidă",
    "Bronșită acută",
    "Alergii sezoniere",
    "Otită medie acută",
    "Lombalgii acute",
    "Angină acută",
    "Conjunctivită bacteriană",
    "Dermatită de contact",
    "Anxietate generalizată",
    "Insomnie cronică",
  ];

  const symptoms = [
    "Dureri de cap persistente",
    "Febră și frisoane",
    "Tuse seacă și dureri în gât",
    "Greață și vărsături",
    "Dureri abdominale",
    "Oboseală cronică",
    "Dureri articulare",
    "Dificultăți de respirație",
    "Dureri toracice",
    "Ameți și vertij",
    "Erupții cutanate",
    "Congestie nazală",
    "Dureri musculare",
    "Palpitații cardiace",
    "Pierdere de apetit",
  ];

  const treatments = [
    "Paracetamol 500mg - 3x/zi",
    "Ibuprofen 400mg - 2x/zi",
    "Amoxicilină 500mg - 3x/zi, 7 zile",
    "Metformină 850mg - 2x/zi",
    "Enalapril 10mg - 1x/zi dimineața",
    "Omeprazol 20mg - 1x/zi înainte de masă",
    "Cetirizină 10mg - 1x/zi seara",
    "Diclofenac gel - aplicare locală 2-3x/zi",
    "Aerosoli cu ser fiziologic - 3x/zi",
    "Regim alimentar hipoglucidic",
    "Repaus la pat 3-5 zile",
    "Hidratare abundentă (min 2L/zi)",
    "Azitromicină 500mg - 1x/zi, 3 zile",
    "Vitamina C 1000mg - 1x/zi",
    "Consultație specialist - cardiologie",
  ];

  const statuses = ["Programat", "În curs", "Finalizat", "Anulat"];

  const getRandomDate = (start: Date, end: Date) => {
    return new Date(
      start.getTime() + Math.random() * (end.getTime() - start.getTime()),
    );
  };

  const currentPatientId = patientId || Math.floor(Math.random() * 100) + 1;
  const consultationCount = Math.floor(Math.random() * 8) + 3; // 3-10 consultations

  return Array.from({ length: consultationCount }, (_, index) => {
    const consultationDate = getRandomDate(
      new Date(Date.now() - 365 * 24 * 60 * 60 * 1000), // 1 year ago
      new Date(),
    );

    const diagnostic =
      diagnostics[Math.floor(Math.random() * diagnostics.length)];
    const symptomCount = Math.floor(Math.random() * 3) + 1; // 1-3 symptoms
    const selectedSymptoms: string[] = [];
    for (let i = 0; i < symptomCount; i++) {
      const symptom = symptoms[Math.floor(Math.random() * symptoms.length)];
      if (!selectedSymptoms.includes(symptom)) {
        selectedSymptoms.push(symptom);
      }
    }

    const treatmentCount = Math.floor(Math.random() * 3) + 1; // 1-3 treatments
    const selectedTreatments: string[] = [];
    for (let i = 0; i < treatmentCount; i++) {
      const treatment =
        treatments[Math.floor(Math.random() * treatments.length)];
      if (!selectedTreatments.includes(treatment)) {
        selectedTreatments.push(treatment);
      }
    }

    return {
      IdCons: index + 1,
      IdPersoana: currentPatientId,
      Data: consultationDate.toISOString(),
      Motivele_prezentarii: selectedSymptoms.join(", "),
      Diagnostic: diagnostic,
      TratamentRecomandat: selectedTreatments.join("; "),
      AlteInformatii: `Consultație ${
        index + 1
      } - ${diagnostic}. Pacient prezintă simptome de ${selectedSymptoms[0].toLowerCase()}.`,
      Status: statuses[Math.floor(Math.random() * statuses.length)],
      Durata_min: Math.floor(Math.random() * 45) + 15, // 15-60 minutes
      Medic: `Dr. ${
        ["Popescu", "Ionescu", "Marinescu", "Dumitrescu", "Georgescu"][
          Math.floor(Math.random() * 5)
        ]
      }`,
      // Additional fields from database that may be returned
      ExamenClinic: null,
      ExamenLaborator: null,
      Ecografie: null,
      Cistoscopie: null,
      Reteta: null,
      TratamentEfectuat: null,
      Anamneza: null,
      FactoriDeRisc: null,
      DopplerVenos: null,
      DopplerArterial: null,
      DopplerCarotidian: null,
      HolterTA: null,
      EKG: null,
      HolterECG: null,
    };
  }).sort((a, b) => new Date(b.Data).getTime() - new Date(a.Data).getTime());
};

export default function ConsultationList({ patientId }: ConsultationListProps) {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState<any[]>([]);
  const [editing, setEditing] = useState<any | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [useMockData, setUseMockData] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [consultationToDelete, setConsultationToDelete] = useState<any | null>(
    null,
  );
  const [deleting, setDeleting] = useState(false);

  const dateLocale = React.useMemo(() => {
    const lang = (i18n.resolvedLanguage || i18n.language || "ro").toLowerCase();
    if (lang.startsWith("ro")) return "ro-RO";
    if (lang.startsWith("fr")) return "fr-FR";
    return "en-US";
  }, [i18n.language, i18n.resolvedLanguage]);

  const loadConsultations = useCallback(async () => {
    console.log(
      `[ConsultationList] Loading consultations for patient: ${
        patientId || "all"
      }`,
    );
    setLoading(true);
    setError(null);

    try {
      // Configure axios request like SampleDataGrid
      const config = {
        timeout: 10000,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          "X-API-Key": process.env.REACT_APP_API_KEY,
        },
      };

      console.log("[ConsultationList] Request config:", {
        timeout: config.timeout,
        hasAuthToken: !!localStorage.getItem("authToken"),
        hasApiKey: !!process.env.REACT_APP_API_KEY,
      });

      // Build API URL
      const apiUrl = patientId
        ? `${API_BASE_URL}${API_ENDPOINTS.getByPatient}/${patientId}`
        : `${API_BASE_URL}${API_ENDPOINTS.consultations}`;

      console.log(`[ConsultationList] Fetching from: ${apiUrl}`);

      // Make API call with axios
      const response = await axios.get(apiUrl, config);

      console.log("[ConsultationList] API Response:", {
        status: response.status,
        dataLength: response.data?.length || 0,
      });

      const consultations = Array.isArray(response.data) ? response.data : [];
      setItems(consultations);
      setUseMockData(false);

      console.log(
        `[ConsultationList] Loaded ${consultations.length} consultations from API`,
      );
      if (consultations.length > 0) {
        console.log(
          "[ConsultationList] First consultation sample:",
          consultations[0],
        );
        console.log(
          "[ConsultationList] First consultation keys:",
          Object.keys(consultations[0]),
        );
      }
    } catch (err) {
      console.error("[ConsultationList] Error loading consultations:", err);

      // Handle 404 as empty list - not an error
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        console.log("[ConsultationList] 404 response - treating as empty list");
        setItems([]);
        setUseMockData(false);
        setError(null);
        setLoading(false);
        return;
      }

      let errorMessage = "Failed to load consultations";

      if (axios.isAxiosError(err)) {
        console.log("[ConsultationList] Axios error details:", {
          message: err.message,
          code: err.code,
          status: err.response?.status,
          url: err.config?.url,
        });

        if (err.response) {
          errorMessage = `Server error: ${err.response.status} ${err.response.statusText}`;
        } else if (err.request) {
          errorMessage = "Network error - please check your connection";
        }
      }

      setError(errorMessage);

      // Fallback to mock data in development
      if (process.env.NODE_ENV === "development") {
        console.warn("[ConsultationList] Using mock data as fallback");
        const mockData = generateMockConsultations(patientId);
        setItems(mockData);
        setUseMockData(true);
        console.log(
          `[ConsultationList] Generated ${mockData.length} mock consultations`,
        );
        if (mockData.length > 0) {
          console.log(
            "[ConsultationList] First mock consultation sample:",
            mockData[0],
          );
          console.log(
            "[ConsultationList] First mock consultation keys:",
            Object.keys(mockData[0]),
          );
        }
      }
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    loadConsultations();
  }, [loadConsultations]);

  const reload = useCallback(() => {
    console.log("[ConsultationList] Reloading consultations...");
    setEditing(null);
    setDialogOpen(false);
    loadConsultations();
  }, [loadConsultations]);

  const handleOpenDialog = (consultation?: any) => {
    console.log(
      "[ConsultationList] Opening dialog with consultation:",
      consultation,
    );
    console.log(
      "[ConsultationList] Consultation keys:",
      consultation ? Object.keys(consultation) : "none",
    );
    console.log(
      "[ConsultationList] Full consultation object:",
      JSON.stringify(consultation, null, 2),
    );
    // If creating new consultation, include patientId
    const initialData =
      consultation ||
      (patientId ? { IdPersoana: patientId, idPersoana: patientId } : {});
    setEditing(initialData);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    console.log("[ConsultationList] Closing dialog");
    setDialogOpen(false);
    // Use setTimeout to clear editing state after dialog animation completes
    setTimeout(() => {
      setEditing(null);
    }, 200);
  };

  const handleSaved = () => {
    reload();
  };

  const handleDeleteClick = (consultation: any) => {
    console.log(
      "[ConsultationList] Opening delete confirmation for:",
      consultation,
    );
    setConsultationToDelete(consultation);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!consultationToDelete) return;

    console.log(
      "[ConsultationList] Deleting consultation:",
      consultationToDelete,
    );
    setDeleting(true);
    setError(null);

    try {
      const config = {
        timeout: 10000,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          "X-API-Key": process.env.REACT_APP_API_KEY,
        },
      };

      const consultationId =
        consultationToDelete.idCons || consultationToDelete.id;
      const apiUrl = `${API_BASE_URL}${API_ENDPOINTS.consultations}/${consultationId}`;

      console.log(`[ConsultationList] Deleting from: ${apiUrl}`);

      await axios.delete(apiUrl, config);

      console.log("[ConsultationList] Consultation deleted successfully");

      // Close dialog and reload
      setDeleteDialogOpen(false);
      setConsultationToDelete(null);
      reload();
    } catch (err) {
      console.error("[ConsultationList] Error deleting consultation:", err);

      let errorMessage = "Failed to delete consultation";

      if (axios.isAxiosError(err)) {
        if (err.response) {
          errorMessage = `Server error: ${err.response.status} ${err.response.statusText}`;
        } else if (err.request) {
          errorMessage = "Network error - please check your connection";
        }
      }

      setError(errorMessage);

      // In development, simulate successful deletion with mock data
      if (process.env.NODE_ENV === "development" && useMockData) {
        console.warn("[ConsultationList] Simulating delete in mock mode");
        setItems(
          items.filter(
            (item) =>
              (item.idCons || item.id) !==
              (consultationToDelete.idCons || consultationToDelete.id),
          ),
        );
        setDeleteDialogOpen(false);
        setConsultationToDelete(null);
        setError(null);
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    console.log("[ConsultationList] Delete cancelled");
    setDeleteDialogOpen(false);
    setConsultationToDelete(null);
  };

  const escapeHtml = (value: unknown) => {
    const text = String(value ?? "");
    return text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  };

  const getField = (obj: any, ...keys: string[]) => {
    for (const key of keys) {
      const value = obj?.[key];
      if (
        value !== undefined &&
        value !== null &&
        String(value).trim() !== ""
      ) {
        return value;
      }
    }
    return undefined;
  };

  const handleBrowserPrint = (consultation: any) => {
    const id = getField(consultation, "idCons", "IdCons", "id") ?? "(fără ID)";
    const dateValue = getField(consultation, "data", "Data");
    const diagnostic = getField(consultation, "diagnostic", "Diagnostic");
    const medic = getField(consultation, "medic", "Medic");
    const durata = getField(consultation, "durata_min", "Durata_min");
    const status = getField(consultation, "Status", "status");

    const motive = getField(
      consultation,
      "motivele_prezentarii",
      "Motivele_prezentarii",
    );
    const anamneza = getField(consultation, "anamneza", "Anamneza");
    const examenClinic = getField(consultation, "examenClinic", "ExamenClinic");
    const examenLaborator = getField(
      consultation,
      "examenLaborator",
      "ExamenLaborator",
    );
    const tratamentRecomandat = getField(
      consultation,
      "tratamentRecomandat",
      "TratamentRecomandat",
    );
    const tratamentEfectuat = getField(
      consultation,
      "tratamentEfectuat",
      "TratamentEfectuat",
    );
    const reteta = getField(consultation, "reteta", "Reteta");
    const alteInformatii = getField(
      consultation,
      "alteInformatii",
      "AlteInformatii",
    );

    const formattedDate = dateValue
      ? new Date(dateValue).toLocaleString(dateLocale, {
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "";

    const printWindow = window.open(
      "",
      "_blank",
      "noopener,noreferrer,width=900,height=650",
    );
    if (!printWindow) {
      window.alert(t("consultations.printPopupBlocked"));
      return;
    }

    const htmlLang = (i18n.resolvedLanguage || i18n.language || "ro")
      .split("-")[0]
      .toLowerCase();

    const html = `<!doctype html>
  <html lang="${escapeHtml(htmlLang)}">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>${escapeHtml(t("consultations.new"))} #${escapeHtml(id)}</title>
    <style>
      :root { --muted: #666; --border: #ddd; }
      * { box-sizing: border-box; }
      body { font-family: Arial, Helvetica, sans-serif; margin: 24px; color: #111; }
      h1 { font-size: 20px; margin: 0 0 6px; }
      .meta { color: var(--muted); font-size: 12px; margin-bottom: 16px; }
      .card { border: 1px solid var(--border); border-radius: 10px; padding: 16px; }
      .row { display: grid; grid-template-columns: 180px 1fr; gap: 10px; padding: 8px 0; border-bottom: 1px solid #f0f0f0; }
      .row:last-child { border-bottom: none; }
      .label { color: var(--muted); font-size: 12px; }
      .value { white-space: pre-wrap; }
      @media print {
        body { margin: 0; }
        .card { border: none; padding: 0; }
      }
    </style>
  </head>
  <body>
    <h1>${escapeHtml(t("consultations.new"))} #${escapeHtml(id)}</h1>
    <div class="meta">
      ${formattedDate ? `📅 ${escapeHtml(formattedDate)}` : ""}
      ${medic ? ` &nbsp; • &nbsp; 👨‍⚕️ Medic: ${escapeHtml(medic)}` : ""}
      ${durata ? ` &nbsp; • &nbsp; ⏱️ ${escapeHtml(durata)} min` : ""}
      ${status ? ` &nbsp; • &nbsp; ${escapeHtml(status)}` : ""}
    </div>
    <div class="card">
      ${diagnostic ? `<div class="row"><div class="label">${escapeHtml(t("consultations.fields.diagnosis"))}</div><div class="value">${escapeHtml(diagnostic)}</div></div>` : ""}
      ${motive ? `<div class="row"><div class="label">${escapeHtml(t("consultations.fields.reason"))}</div><div class="value">${escapeHtml(motive)}</div></div>` : ""}
      ${anamneza ? `<div class="row"><div class="label">${escapeHtml(t("consultations.fields.anamnesis"))}</div><div class="value">${escapeHtml(anamneza)}</div></div>` : ""}
      ${examenClinic ? `<div class="row"><div class="label">${escapeHtml(t("consultations.fields.clinicalExam"))}</div><div class="value">${escapeHtml(examenClinic)}</div></div>` : ""}
      ${examenLaborator ? `<div class="row"><div class="label">${escapeHtml(t("consultations.fields.labExam"))}</div><div class="value">${escapeHtml(examenLaborator)}</div></div>` : ""}
      ${tratamentRecomandat ? `<div class="row"><div class="label">${escapeHtml(t("consultations.fields.recommendedTreatment"))}</div><div class="value">${escapeHtml(tratamentRecomandat)}</div></div>` : ""}
      ${tratamentEfectuat ? `<div class="row"><div class="label">${escapeHtml(t("consultations.fields.performedTreatment"))}</div><div class="value">${escapeHtml(tratamentEfectuat)}</div></div>` : ""}
      ${reteta ? `<div class="row"><div class="label">${escapeHtml(t("consultations.fields.prescription"))}</div><div class="value">${escapeHtml(reteta)}</div></div>` : ""}
      ${alteInformatii ? `<div class="row"><div class="label"> </div><div class="value">${escapeHtml(alteInformatii)}</div></div>` : ""}
    </div>
    <script>
      window.addEventListener('load', () => {
        window.focus();
        window.print();
      });
    </script>
  </body>
</html>`;

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  };

  const handlePrint = (consultation: any) => {
    const traceId = `ssrs-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const startedAt =
      typeof performance !== "undefined" &&
      typeof performance.now === "function"
        ? performance.now()
        : Date.now();
    const logPrefix = `[ConsultationList][SSRS][${traceId}]`;

    // High-signal trace logging (avoid printing secrets like passwords).
    try {
      console.groupCollapsed(`${logPrefix} Print requested`);
    } catch {
      // Some environments don't support console groups.
    }
    console.log(`${logPrefix} Input`, {
      hasConsultation: !!consultation,
      patientId,
      consultationKeys: consultation ? Object.keys(consultation) : [],
    });

    // SQL Server Reporting Services (SSRS) / Report Server printing.
    // Configure these in your environment (.env / IIS env vars) and rebuild:
    // - REACT_APP_SSRS_BASE_URL (e.g. http://datastudio.ro/ReportServer)
    // - REACT_APP_SSRS_CONSULTATION_REPORT_PATH (e.g. /DataStudio/ConsultationPrint)
    // - REACT_APP_SSRS_CODUNITATE (e.g. 101)
    // Optional Windows/Basic auth (prefer a backend proxy for real Windows/NTLM auth):
    // - REACT_APP_SSRS_USERNAME / REACT_APP_SSRS_PASSWORD
    // Optional overrides:
    // - REACT_APP_SSRS_PARAM_IDCONSULTATIE (default: IdConsultatie)
    // - REACT_APP_SSRS_PARAM_CODUNITATE (default: CodUnitate)
    // - REACT_APP_SSRS_PARAM_IDPERSOANA (default: IdPersoana)
    // - REACT_APP_SSRS_CONSULTATION_FORMAT (default: PDF)
    const ssrsBaseUrl =
      process.env.REACT_APP_SSRS_BASE_URL ||
      "http://datastudio.ro/ReportServer";
    const reportPath = process.env.REACT_APP_SSRS_CONSULTATION_REPORT_PATH;
    const format = process.env.REACT_APP_SSRS_CONSULTATION_FORMAT || "PDF";

    const ssrsUsername =
      process.env.REACT_APP_SSRS_USERNAME ||
      // Fallback for a common typo/misnamed env var.
      (process.env as any).REACT_APP_SSRS_USERNAM ||
      "";
    const ssrsPassword = process.env.REACT_APP_SSRS_PASSWORD || "";
    const hasSsrsCredentials =
      String(ssrsUsername).trim().length > 0 &&
      String(ssrsPassword).trim().length > 0;

    // When SSRS is accessed via a backend proxy that can't receive headers from
    // `window.open()`, we can optionally send SSRS credentials as query params.
    // IMPORTANT: keep this opt-in because credentials in URLs can be logged by servers.
    const ssrsSendAuthParam =
      String(process.env.REACT_APP_SSRS_SEND_AUTH_PARAM || "")
        .trim()
        .toLowerCase() === "true";
    const ssrsAuthUsernameParamName =
      process.env.REACT_APP_SSRS_USERNAME || "username";
    const ssrsAuthPasswordParamName =
      process.env.REACT_APP_SSRS_PASSWORD || "password";

    console.log(`${logPrefix} Config`, {
      ssrsBaseUrl,
      reportPath,
      format,
      hasSsrsCredentials,
      ssrsUsernameProvided: String(ssrsUsername).trim().length > 0,
    });

    const idConsultatieParamName =
      process.env.REACT_APP_SSRS_PARAM_IDCONSULTATIE || "IdConsultatie";
    const codUnitateParamName =
      process.env.REACT_APP_SSRS_PARAM_CODUNITATE || "CodUnitate";
    const idPersoanaParamName =
      process.env.REACT_APP_SSRS_PARAM_IDPERSOANA || "IdPersoana";
    const doctorParamName = "DoctorName"; // Optional additional param for doctor's name

    console.log(`${logPrefix} Param names`, {
      idConsultatieParamName,
      codUnitateParamName,
      idPersoanaParamName,
      doctorParamName,
    });

    const doctorName = getField(consultation, "Medic", "medic") || "N/A";
    const idPersoana =
      getField(consultation, "IdPersoana", "idPersoana") ?? patientId;
    const codUnitate =
      getField(consultation, "CodUnitate", "codUnitate") ??
      process.env.REACT_APP_SSRS_CODUNITATE ??
      localStorage.getItem("CodUnitate") ??
      localStorage.getItem("codUnitate") ??
      24;
    const idConsultatie = getField(
      consultation,
      "IdConsultatie",
      "idConsultatie",
      "IdCons",
      "idCons",
      "id",
    );

    console.log(`${logPrefix} Resolved params`, {
      idConsultatie,
      codUnitate,
      idPersoana,
      codUnitateSource: getField(consultation, "CodUnitate", "codUnitate")
        ? "consultation"
        : process.env.REACT_APP_SSRS_CODUNITATE
          ? "env(REACT_APP_SSRS_CODUNITATE)"
          : localStorage.getItem("CodUnitate") ||
              localStorage.getItem("codUnitate")
            ? "localStorage"
            : "fallback(default)",
      idPersoanaSource: getField(consultation, "IdPersoana", "idPersoana")
        ? "consultation"
        : patientId
          ? "props(patientId)"
          : "missing",
      idConsultatieSource: getField(
        consultation,
        "IdConsultatie",
        "idConsultatie",
      )
        ? "consultation(IdConsultatie)"
        : getField(consultation, "IdCons", "idCons")
          ? "consultation(IdCons)"
          : getField(consultation, "id")
            ? "consultation(id)"
            : "missing",
    });

    if (!reportPath) {
      console.warn(
        `${logPrefix} Missing reportPath; fallback to browser print`,
        {
          reportPath,
          ssrsBaseUrl,
        },
      );
      window.alert(
        "Printarea prin SQL Report Server nu este configurată. Setează REACT_APP_SSRS_CONSULTATION_REPORT_PATH și reconstruiește aplicația.",
      );

      // Fallback for dev environments.
      handleBrowserPrint(consultation);

      const elapsedMs =
        (typeof performance !== "undefined" &&
        typeof performance.now === "function"
          ? performance.now()
          : Date.now()) - startedAt;
      console.log(`${logPrefix} Done (fallback browser print)`, {
        elapsedMs: Math.round(elapsedMs),
      });
      try {
        console.groupEnd();
      } catch {
        // ignore
      }
      return;
    }

    const missing: string[] = [];
    if (idConsultatie === undefined || String(idConsultatie).trim() === "") {
      missing.push(idConsultatieParamName);
    }
    if (codUnitate === undefined || String(codUnitate).trim() === "") {
      missing.push(codUnitateParamName);
    }
    if (idPersoana === undefined || String(idPersoana).trim() === "") {
      missing.push(idPersoanaParamName);
    }
    if (missing.length > 0) {
      console.warn(`${logPrefix} Missing SSRS params`, {
        missing,
        resolved: { idConsultatie, codUnitate, idPersoana },
      });
      window.alert(
        `Nu se poate printa: lipsesc parametrii SSRS: ${missing.join(", ")}.`,
      );

      const elapsedMs =
        (typeof performance !== "undefined" &&
        typeof performance.now === "function"
          ? performance.now()
          : Date.now()) - startedAt;
      console.log(`${logPrefix} Done (missing params)`, {
        elapsedMs: Math.round(elapsedMs),
      });
      try {
        console.groupEnd();
      } catch {
        // ignore
      }
      return;
    }

    const normalizedBase = ssrsBaseUrl.replace(/\/+$/, "");
    const normalizedPath = reportPath.startsWith("/")
      ? reportPath
      : `/${reportPath}`;

    // SSRS uses a special URL shape: /ReportServer?/Folder/Report&key=value...
    // Keep slashes in the report path but encode spaces and unsafe chars.
    const reportPathEncoded = encodeURI(normalizedPath);
    const query = [
      reportPathEncoded,
      "rs:Command=Render",
      "rc:Parameters=false",
      `${encodeURIComponent(idConsultatieParamName)}=${encodeURIComponent(
        String(idConsultatie),
      )}`,
      `${encodeURIComponent(codUnitateParamName)}=${encodeURIComponent(
        String(codUnitate),
      )}`,
      `${encodeURIComponent(idPersoanaParamName)}=${encodeURIComponent(
        String(idPersoana),
      )}`,
      `${encodeURIComponent(doctorParamName)}=${encodeURIComponent(
        String(doctorName),
      )}`,
    ].join("&");

    const url = `${normalizedBase}?${query}`;

    const urlForLogs = (() => {
      if (!(ssrsSendAuthParam && hasSsrsCredentials)) return url;
      try {
        const [base, qs] = url.split("?", 2);
        if (!qs) return url;
        const parts = qs.split("&");
        const userKey = encodeURIComponent(ssrsAuthUsernameParamName);
        const passKey = encodeURIComponent(ssrsAuthPasswordParamName);
        const redacted = parts.map((p) =>
          p.startsWith(`${userKey}=`)
            ? `${userKey}=<redacted>`
            : p.startsWith(`${passKey}=`)
              ? `${passKey}=<redacted>`
              : p,
        );
        return `${base}?${redacted.join("&")}`;
      } catch {
        return `${normalizedBase}?(query redacted)`;
      }
    })();
    console.log(`${logPrefix} Built SSRS URL`, {
      normalizedBase,
      normalizedPath,
      reportPathEncoded,
      url: urlForLogs,
      ssrsSendAuthParam,
      ssrsAuthUsernameParamName: ssrsSendAuthParam
        ? ssrsAuthUsernameParamName
        : undefined,
      ssrsAuthPasswordParamName: ssrsSendAuthParam
        ? ssrsAuthPasswordParamName
        : undefined,
      hasSsrsCredentials,
    });

    // Open a popup synchronously (so it isn't blocked), then either:
    // - fetch the PDF using Basic auth (if configured), OR
    // - navigate directly to SSRS (browser handles interactive auth).
    const printWindow = window.open(
      "about:blank",
      "ssrs-print",
      "popup=yes,width=1100,height=800,scrollbars=yes,resizable=yes",
    );
    if (!printWindow) {
      console.error(`${logPrefix} Popup blocked while opening report`, {
        url,
      });
      window.alert(t("consultations.printPopupBlocked"));

      const elapsedMs =
        (typeof performance !== "undefined" &&
        typeof performance.now === "function"
          ? performance.now()
          : Date.now()) - startedAt;
      console.log(`${logPrefix} Done (popup blocked)`, {
        elapsedMs: Math.round(elapsedMs),
      });
      try {
        console.groupEnd();
      } catch {
        // ignore
      }
      return;
    }

    try {
      printWindow.document.title = "SSRS Print";
      printWindow.document.body.innerHTML =
        "<p style='font-family: Arial, sans-serif; padding: 16px;'>Loading report…</p>";
    } catch {
      // Ignore cross-window document access issues.
    }

    // Always navigate the popup directly to SSRS.
    // Rationale: SSRS HTML viewer depends on additional assets; fetching into a blob URL
    // often breaks those relative resources (common in IIS/prod).
    console.log(`${logPrefix} Navigating popup to SSRS URL (no fetch/blob)`, {
      url,
      hasSsrsCredentials,
      ssrsUsernameProvided: String(ssrsUsername).trim().length > 0,
      format,
    });

    printWindow.location.href = url;
    printWindow.focus();

    const elapsedMs =
      (typeof performance !== "undefined" &&
      typeof performance.now === "function"
        ? performance.now()
        : Date.now()) - startedAt;
    console.log(`${logPrefix} Done (navigated to SSRS)`, {
      elapsedMs: Math.round(elapsedMs),
    });
    try {
      console.groupEnd();
    } catch {
      // ignore
    }
    return;
  };

  return (
    <Box sx={{ p: 2 }}>
      {error && !useMockData && (
        <Alert severity="error" sx={{ mb: 2 }}>
          ❌ {error}
        </Alert>
      )}

      {useMockData && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t("consultations.mockDataInfo", {
            count: items.length,
            patientId: patientId ?? t("consultations.unknownPatient"),
          })}
        </Alert>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h6">
          {t("consultations.title")} ({items.length})
          {loading && <CircularProgress size={20} sx={{ ml: 2 }} />}
        </Typography>
        <Button variant="contained" onClick={() => handleOpenDialog()}>
          {t("consultations.new")}
        </Button>
      </Box>

      {items.length === 0 ? (
        <Alert severity="info">{t("consultations.emptyForPatient")}</Alert>
      ) : (
        <List>
          {items.map((it) => (
            <ListItem
              key={it.idCons || it.id}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                mb: 1,
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              }}
            >
              <ListItemText
                primary={
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {it.diagnostic
                        ? it.diagnostic
                            .toLowerCase()
                            .replace(/\b\w/g, (char: string) =>
                              char.toUpperCase(),
                            )
                        : t("consultations.defaultTitle")}
                    </Typography>
                    {it.data && (
                      <Typography variant="caption" color="text.secondary">
                        📅{" "}
                        {new Date(it.data).toLocaleDateString(dateLocale, {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                        {it.medic && ` • 👨‍⚕️ ${it.medic}`}
                        {it.durata_min && ` • ⏱️ ${it.durata_min} min`}
                      </Typography>
                    )}
                  </Box>
                }
                secondary={
                  <Box sx={{ mt: 1 }}>
                    {it.motivele_prezentarii && (
                      <Typography variant="body2" color="text.secondary">
                        <strong>{t("consultations.fields.reason")}:</strong>{" "}
                        {it.motivele_prezentarii}
                      </Typography>
                    )}
                    {it.anamneza && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        <strong>{t("consultations.fields.anamnesis")}:</strong>{" "}
                        {it.anamneza}
                      </Typography>
                    )}
                    {it.examenClinic && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        <strong>
                          {t("consultations.fields.clinicalExam")}:
                        </strong>{" "}
                        {it.examenClinic}
                      </Typography>
                    )}
                    {it.examenLaborator && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        <strong>{t("consultations.fields.labExam")}:</strong>{" "}
                        {it.examenLaborator}
                      </Typography>
                    )}
                    {it.tratamentRecomandat && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        <strong>
                          {t("consultations.fields.recommendedTreatment")}:
                        </strong>{" "}
                        {it.tratamentRecomandat}
                      </Typography>
                    )}
                    {it.tratamentEfectuat && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        <strong>
                          {t("consultations.fields.performedTreatment")}:
                        </strong>{" "}
                        {it.tratamentEfectuat}
                      </Typography>
                    )}
                    {it.reteta && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        <strong>
                          {t("consultations.fields.prescription")}:
                        </strong>{" "}
                        {it.reteta}
                      </Typography>
                    )}
                    {it.alteInformatii && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5, fontStyle: "italic" }}
                      >
                        {it.alteInformatii}
                      </Typography>
                    )}

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                        gap: 1,
                        mt: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      {it.Status && (
                        <Chip
                          label={it.Status}
                          size="small"
                          color={
                            it.Status === "Finalizat"
                              ? "success"
                              : it.Status === "În curs"
                                ? "primary"
                                : it.Status === "Anulat"
                                  ? "error"
                                  : "default"
                          }
                        />
                      )}
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<PrintIcon />}
                        onClick={() => handlePrint(it)}
                      >
                        {t("common.print")}
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleOpenDialog(it)}
                      >
                        {t("common.edit")}
                      </Button>
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleDeleteClick(it)}
                        aria-label="delete"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      )}

      {/* Consultation Form Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: "600px",
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">
              {editing?.IdCons || editing?.idCons || editing?.id
                ? t("consultations.editTitle")
                : t("consultations.newTitle")}
            </Typography>
            <IconButton
              edge="end"
              color="inherit"
              onClick={handleCloseDialog}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ minHeight: "500px" }}>
          {editing !== null && (
            <ConsultationForm initial={editing} onSaved={handleSaved} />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("consultations.deleteConfirmTitle")}</DialogTitle>
        <DialogContent>
          <Typography>{t("consultations.deleteConfirmText")}</Typography>
          {consultationToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: "action.hover", borderRadius: 1 }}>
              <Typography variant="body2" fontWeight={600}>
                {consultationToDelete.diagnostic || t("consultations.new")}
              </Typography>
              {consultationToDelete.data && (
                <Typography variant="caption" color="text.secondary">
                  {new Date(consultationToDelete.data).toLocaleDateString(
                    dateLocale,
                    {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    },
                  )}
                </Typography>
              )}
            </Box>
          )}
          <Alert severity="warning" sx={{ mt: 2 }}>
            {t("consultations.cannotUndo")}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={
              deleting ? <CircularProgress size={16} /> : <DeleteIcon />
            }
          >
            {deleting ? t("consultations.deleting") : t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
